import { useState } from 'react';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [charCount, setCharCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'message') {
      setCharCount(value.length);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      if (formData.message) {
        formBody.append('message', formData.message);
      }

      const response = await fetch('https://readdy.ai/api/form/d5horihia7n1eqcquoig', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-rose-50">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            alt="Familia conectando"
            className="w-full h-full object-cover object-center"
            src="https://public.readdy.ai/ai/img_res/8b66f4ba18594255b708917c7775945f.jpg"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/60 to-white/50"></div>
        </div>

        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <img
                alt="Logo"
                className="h-10 sm:h-12 w-auto"
                src="https://public.readdy.ai/ai/img_res/8dee5cc1-3646-466d-be88-4d9c0487265e.png"
              />
              <span className="font-serif text-base sm:text-xl font-semibold text-gray-900">
                El poder de ser yo
              </span>
            </div>
            <button
              onClick={() => scrollToSection('cta')}
              className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-orange-500 to-rose-500 text-white rounded-full font-semibold text-sm sm:text-base transition-all whitespace-nowrap cursor-pointer hover:shadow-xl hover:scale-105 active:scale-95 animate-pulse"
            >
              Comprar ahora
            </button>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center pt-20 sm:pt-0">
          <div className="inline-block mb-4 sm:mb-6 px-4 sm:px-5 py-2 bg-emerald-600/90 backdrop-blur-sm rounded-full">
            <span className="text-white text-xs sm:text-sm font-medium tracking-wide">
              Ebook de crianza consciente
            </span>
          </div>

          <h1 className="font-serif text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
            🌱 El poder de ser yo
          </h1>

          <p className="text-lg sm:text-xl md:text-2xl text-gray-800 font-medium mb-6 sm:mb-10 max-w-3xl mx-auto leading-relaxed">
            Un libro para fortalecer el amor propio, la calma y la conexión emocional en familia
          </p>

          <p className="text-base sm:text-lg text-gray-700 mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed">
            En un mundo lleno de prisa, comparación y sobreestimulación, este libro se convierte en un refugio de calma para niños y padres.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-4">
            <button
              onClick={() => scrollToSection('cta')}
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-orange-500 to-rose-500 text-white rounded-full text-base sm:text-lg font-bold hover:shadow-2xl transition-all whitespace-nowrap cursor-pointer hover:scale-110 active:scale-95 shadow-lg"
            >
              🎁 Obtener por $9 USD
            </button>
            <button
              onClick={() => scrollToSection('content')}
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white border-2 border-gray-900 text-gray-900 rounded-full text-base sm:text-lg font-bold hover:bg-gray-900 hover:text-white transition-all whitespace-nowrap cursor-pointer hover:scale-105 active:scale-95 shadow-md"
            >
              Ver contenido
            </button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce hidden sm:block">
          <div className="w-6 h-10 border-2 border-gray-900 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-gray-900 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Why Different Section */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-gradient-to-b from-rose-50 to-amber-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <p className="text-orange-600 text-xs sm:text-sm font-bold tracking-widest uppercase mb-3 sm:mb-4">
              ¿Por qué este libro es diferente?
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 sm:mb-8 leading-tight max-w-4xl mx-auto">
              En un mundo lleno de prisa,<br />
              este libro es un refugio<br />
              de calma para tu familia
            </h2>
          </div>

          <div className="max-w-3xl mx-auto mb-8 sm:mb-12 md:mb-16">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-lg">
              <p className="text-base sm:text-lg md:text-xl text-gray-800 leading-relaxed mb-4 sm:mb-6 font-medium">
                Porque enseña a los niños que son valiosos por lo que son y no por lo que hacen, y acompaña a los padres a criar desde la comprensión y no desde el control.
              </p>
              <div className="flex items-center gap-3 sm:gap-4 text-base sm:text-lg">
                <span className="text-2xl sm:text-3xl">💛</span>
                <p className="text-gray-700 italic font-medium">No busca niños perfectos ni padres perfectos.</p>
              </div>
              <div className="flex items-center gap-3 sm:gap-4 text-base sm:text-lg mt-2 sm:mt-3">
                <span className="text-2xl sm:text-3xl">✨</span>
                <p className="text-gray-700 italic font-bold">Busca conexión real.</p>
              </div>
            </div>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-6">
            {[
              { icon: 'ri-heart-3-line', text: 'Bajar el ritmo' },
              { icon: 'ri-ear-line', text: 'Escuchar con atención' },
              { icon: 'ri-emotion-line', text: 'Validar emociones' },
              { icon: 'ri-group-line', text: 'Compartir momentos' },
              { icon: 'ri-seedling-line', text: 'Criar desde el amor' }
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-md hover:shadow-xl transition-all text-center group cursor-pointer hover:scale-105"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 flex items-center justify-center mx-auto mb-2 sm:mb-3 md:mb-4 text-emerald-600 text-2xl sm:text-3xl md:text-4xl group-hover:scale-110 transition-transform">
                  <i className={feature.icon}></i>
                </div>
                <p className="text-gray-900 font-bold text-xs sm:text-sm md:text-base">{feature.text}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 sm:mt-12 md:mt-16">
            <p className="text-lg sm:text-xl md:text-2xl text-gray-800 font-medium italic max-w-3xl mx-auto">
              Cada página invita a crear momentos genuinos en familia
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section id="content" className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-gradient-to-b from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 sm:gap-8 md:gap-12 mb-8 sm:mb-12 md:mb-16">
            <div className="lg:w-1/2">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                📘 ¿Qué encontrarás<br />dentro del ebook?
              </h2>
            </div>
            <div className="lg:w-1/2 flex items-center">
              <p className="text-base sm:text-lg md:text-xl text-gray-800 leading-relaxed font-medium">
                Cada capítulo está diseñado para que padres e hijos vivan juntos un proceso de autodescubrimiento, fortaleciendo su vínculo emocional.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">
            {/* Main Content Card */}
            <div className="lg:col-span-5 bg-emerald-50 rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all">
              <div className="h-48 sm:h-56 md:h-64">
                <img
                  alt="Cuentos y reflexiones"
                  className="w-full h-full object-cover"
                  src="https://public.readdy.ai/ai/img_res/8875f87045b21d33a687adcf1317f9e5.jpg"
                />
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
                  Cuentos y reflexiones
                </h3>
                <ul className="space-y-3 sm:space-y-4">
                  {[
                    'Cuentos y reflexiones que enseñan el valor de la autenticidad',
                    'Ejercicios prácticos para fortalecer la autoestima y la empatía',
                    'Actividades familiares que fomentan la unión y la comunicación emocional',
                    'Herramientas de inteligencia emocional explicadas de forma sencilla y cercana',
                    'Espacios para escribir y reflexionar, donde los niños pueden expresar lo que sienten con libertad'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-2 sm:gap-3">
                      <span className="text-emerald-600 text-lg sm:text-xl mt-0.5 font-bold">✔️</span>
                      <span className="text-gray-800 leading-relaxed text-sm sm:text-base font-medium">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* For Children and Parents Cards */}
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              {/* For Children */}
              <div className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:scale-105">
                <div className="p-6 sm:p-8 text-center">
                  <h3 className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
                    Para los niños
                  </h3>
                </div>
                <div className="h-48 sm:h-56">
                  <img
                    alt="Para los niños"
                    className="w-full h-full object-cover"
                    src="https://public.readdy.ai/ai/img_res/0cae54c07439ca8b477028bd906f9857.jpg"
                  />
                </div>
                <div className="p-6 sm:p-8">
                  <ul className="space-y-3 sm:space-y-4">
                    {[
                      { icon: 'ri-emotion-happy-line', text: 'Aprenden a reconocer y expresar sus emociones' },
                      { icon: 'ri-heart-3-line', text: 'Fortalecen su amor propio y confianza' },
                      { icon: 'ri-user-smile-line', text: 'Se sienten escuchados, validados y comprendidos' }
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-2 sm:gap-3">
                        <div className="w-6 h-6 flex items-center justify-center text-emerald-600 text-lg sm:text-xl flex-shrink-0">
                          <i className={item.icon}></i>
                        </div>
                        <span className="text-gray-800 font-medium text-sm sm:text-base">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* For Parents */}
              <div className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:scale-105">
                <div className="p-6 sm:p-8 text-center">
                  <h3 className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
                    Para los padres
                  </h3>
                </div>
                <div className="h-48 sm:h-56">
                  <img
                    alt="Para los padres"
                    className="w-full h-full object-cover"
                    src="https://readdy.ai/api/search-image?query=soft%20watercolor%20illustration%20peaceful%20parent%20listening%20empathy%20understanding%20calm%20presence%20gentle%20artistic%20style%20warm%20pastel%20colors%20simple%20minimalist%20design%20emotional%20support%20patience%20mindful%20parenting%20serene%20atmosphere%20light%20background&width=400&height=350&seq=parents-benefits-001&orientation=portrait"
                  />
                </div>
                <div className="p-6 sm:p-8">
                  <ul className="space-y-3 sm:space-y-4">
                    {[
                      { icon: 'ri-lightbulb-line', text: 'Descubren herramientas simples para acompañar con empatía' },
                      { icon: 'ri-ear-line', text: 'Aprenden a escuchar sin juzgar' },
                      { icon: 'ri-leaf-line', text: 'Cultivan la calma incluso en momentos difíciles' }
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-2 sm:gap-3">
                        <div className="w-6 h-6 flex items-center justify-center text-orange-600 text-lg sm:text-xl flex-shrink-0">
                          <i className={item.icon}></i>
                        </div>
                        <span className="text-gray-800 font-medium text-sm sm:text-base">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-gradient-to-b from-orange-50 to-yellow-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
              🌟 Beneficios de<br />"El poder de ser yo"
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-800 max-w-3xl mx-auto leading-relaxed font-medium">
              Este libro promueve una crianza consciente, donde la prioridad es la conexión emocional, el respeto y la comprensión.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12 md:mb-16">
            {[
              { icon: 'ri-book-open-line', bg: 'bg-emerald-100', color: 'text-emerald-600', title: 'Acceso inmediato', desc: '📥 Descarga instantánea después de la compra. Comienza a leer de inmediato.' },
              { icon: 'ri-smartphone-line', bg: 'bg-orange-100', color: 'text-orange-600', title: 'Formato digital', desc: '📱 Puedes usarlo en cualquier dispositivo: tablet, móvil o computadora.' },
              { icon: 'ri-group-2-line', bg: 'bg-rose-100', color: 'text-rose-600', title: 'Para toda la familia', desc: '👨‍👩‍👧 Ideal para leer y trabajar en familia. Experiencia compartida.' }
            ].map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all text-center group hover:scale-105"
              >
                <div className={`w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mx-auto mb-4 sm:mb-6 ${benefit.bg} rounded-full ${benefit.color} text-3xl sm:text-4xl group-hover:scale-110 transition-transform`}>
                  <i className={benefit.icon}></i>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                  {benefit.title}
                </h3>
                <p className="text-gray-800 leading-relaxed text-sm sm:text-base font-medium">
                  {benefit.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Pricing Card */}
          <div className="relative bg-gradient-to-br from-orange-500 via-rose-500 to-pink-600 rounded-3xl p-1 shadow-2xl animate-pulse">
            <div className="bg-white rounded-3xl p-8 sm:p-10 md:p-12 relative overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-200 to-rose-200 rounded-full blur-3xl opacity-50"></div>
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-pink-200 to-orange-200 rounded-full blur-3xl opacity-50"></div>
              
              <div className="relative z-10">
                {/* Urgency Badge */}
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-2 rounded-full mb-6 shadow-lg animate-bounce">
                  <i className="ri-fire-fill text-xl"></i>
                  <span className="font-bold text-sm sm:text-base">¡OFERTA POR TIEMPO LIMITADO!</span>
                  <i className="ri-fire-fill text-xl"></i>
                </div>

                {/* Price Section */}
                <div className="mb-8">
                  <p className="text-gray-600 text-base sm:text-lg font-semibold mb-3 line-through">
                    Precio regular: $47 USD
                  </p>
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <span className="text-6xl sm:text-7xl md:text-8xl font-black bg-gradient-to-r from-orange-600 to-rose-600 bg-clip-text text-transparent">
                      $9
                    </span>
                    <div className="text-left">
                      <span className="block text-2xl sm:text-3xl font-bold text-gray-900">USD</span>
                      <span className="block text-sm text-emerald-600 font-bold">¡81% OFF!</span>
                    </div>
                  </div>
                  <p className="text-lg sm:text-xl text-gray-800 font-bold mb-2">
                    💝 Una inversión pequeña para un impacto profundo
                  </p>
                  <p className="text-base text-gray-600 font-medium">
                    en la relación con tus hijos
                  </p>
                </div>

                {/* Main CTA Button */}
                <button
                  onClick={() => scrollToSection('cta')}
                  className="group relative w-full sm:w-auto px-12 py-5 bg-gradient-to-r from-orange-500 via-rose-500 to-pink-500 text-white rounded-full text-xl sm:text-2xl font-black shadow-2xl hover:shadow-orange-500/50 transition-all cursor-pointer mb-8 overflow-hidden animate-pulse hover:animate-none hover:scale-110 active:scale-95"
                >
                  <span className="relative z-10 flex items-center justify-center gap-3 whitespace-nowrap">
                    <i className="ri-shopping-cart-fill text-2xl"></i>
                    ¡QUIERO MI EBOOK AHORA!
                    <i className="ri-arrow-right-line text-2xl group-hover:translate-x-2 transition-transform"></i>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-500 via-rose-500 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </button>

                {/* Trust Badges */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                  {[
                    { icon: 'ri-shield-check-fill', text: 'Pago 100% seguro', color: 'text-emerald-600' },
                    { icon: 'ri-download-cloud-fill', text: 'Acceso inmediato', color: 'text-blue-600' },
                    { icon: 'ri-infinity-fill', text: 'Acceso de por vida', color: 'text-purple-600' }
                  ].map((badge, index) => (
                    <div key={index} className="flex items-center justify-center gap-2 bg-gray-50 px-4 py-3 rounded-xl">
                      <i className={`${badge.icon} text-2xl ${badge.color}`}></i>
                      <span className="text-sm font-bold text-gray-900">{badge.text}</span>
                    </div>
                  ))}
                </div>

                {/* Benefits List */}
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 mb-6">
                  <h4 className="font-bold text-xl text-gray-900 mb-4 text-center">
                    ✨ Todo lo que obtienes HOY:
                  </h4>
                  <div className="space-y-3">
                    {[
                      'Ebook completo en formato digital',
                      'Acceso inmediato después del pago',
                      'Compatible con todos los dispositivos',
                      'Actualizaciones futuras GRATIS',
                      'Sin suscripciones ni pagos ocultos',
                      'Garantía de satisfacción'
                    ].map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-white text-sm font-bold"></i>
                        </div>
                        <span className="text-gray-800 font-semibold text-sm sm:text-base">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Social Proof */}
                <div className="flex items-center justify-center gap-2 text-gray-700">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-rose-400 border-2 border-white flex items-center justify-center text-white font-bold text-sm">
                        {String.fromCharCode(64 + i)}
                      </div>
                    ))}
                  </div>
                  <p className="text-sm font-bold">
                    <span className="text-orange-600">+500 familias</span> ya transformaron su conexión
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-gradient-to-b from-rose-50 to-amber-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
              💬 Lo que dicen las familias<br />que ya lo leyeron
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-800 max-w-3xl mx-auto font-medium">
              Testimonios reales de padres que transformaron su conexión familiar
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {[
              { name: 'María González', role: 'Mamá de dos niños', initial: 'M', text: '"Este libro nos ayudó a tener conversaciones que nunca habíamos tenido. Mi hijo ahora expresa lo que siente sin miedo."' },
              { name: 'Carlos Ramírez', role: 'Papá de una niña', initial: 'C', text: '"Lo leemos antes de dormir. Se ha convertido en nuestro momento favorito del día."' },
              { name: 'Ana Martínez', role: 'Madre de familia', initial: 'A', text: '"Como mamá, me enseñó a acompañar desde la calma y no desde la reacción."' },
              { name: 'Laura Fernández', role: 'Mamá y educadora', initial: 'L', text: '"No es solo un libro para niños, es una guía para toda la familia."' }
            ].map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all group hover:scale-105"
              >
                <div className="flex gap-1 mb-4 sm:mb-6">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-orange-500 text-lg sm:text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-800 text-base sm:text-lg leading-relaxed mb-4 sm:mb-6 italic font-medium">
                  {testimonial.text}
                </p>
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white font-bold text-lg sm:text-xl flex-shrink-0">
                    {testimonial.initial}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm sm:text-base">{testimonial.name}</p>
                    <p className="text-gray-700 text-xs sm:text-sm font-medium">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-gradient-to-b from-amber-50 to-orange-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
              ❓ Preguntas frecuentes
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-800 font-medium">
              Resolvemos tus dudas sobre el ebook
            </p>
          </div>

          <div className="space-y-4 sm:space-y-6">
            {[
              { q: '¿Para qué edades está recomendado?', a: 'Está diseñado principalmente para niños entre 4 y 10 años, pero sus mensajes y actividades pueden adaptarse a distintas edades.' },
              { q: '¿Es solo para niños?', a: 'No. Es una experiencia compartida. Padres y cuidadores también aprenden y sanan durante el proceso.' },
              { q: '¿Necesito conocimientos previos?', a: 'No. Todo está explicado de forma sencilla, cercana y práctica.' },
              { q: '¿Cómo recibo el ebook?', a: 'Una vez realizada la compra, recibirás acceso inmediato al libro en formato digital.' },
              { q: '¿Puedo usarlo varias veces?', a: 'Sí. Puedes volver a leerlo, repetir actividades y adaptarlo a cada etapa del niño.' }
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 sm:px-8 py-5 sm:py-6 flex items-center justify-between gap-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <span className="font-bold text-gray-900 text-base sm:text-lg md:text-xl pr-4">
                    {faq.q}
                  </span>
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-gradient-to-r from-orange-500 to-rose-500 rounded-full text-white flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-xl sm:text-2xl"></i>
                  </div>
                </button>
                <div
                  className={`transition-all duration-300 ease-in-out ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}
                >
                  <div className="px-6 sm:px-8 pb-5 sm:pb-6">
                    <p className="text-gray-800 leading-relaxed text-sm sm:text-base md:text-lg font-medium">
                      {faq.a}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 sm:mt-12">
            <button
              onClick={() => scrollToSection('cta')}
              className="inline-block px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-orange-500 to-rose-500 text-white rounded-full text-base sm:text-lg font-bold hover:shadow-2xl transition-all whitespace-nowrap cursor-pointer hover:scale-110 active:scale-95 shadow-lg"
            >
              🎁 Obtener el ebook ahora
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-gradient-to-b from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          {/* Hero CTA */}
          <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden mb-8 sm:mb-12 md:mb-16 shadow-2xl">
            <div className="absolute inset-0">
              <img
                alt="Familia conectando"
                className="w-full h-full object-cover"
                src="https://public.readdy.ai/ai/img_res/ae9c108ef8d09c3ce1ae65791e4d948b.jpg"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-800/70 to-gray-900/80"></div>
            </div>
            <div className="relative z-10 py-12 sm:py-16 md:py-24 px-4 sm:px-8 md:px-16">
              <div className="max-w-4xl">
                <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 md:mb-8 leading-tight">
                  🌿 Regálale a tu hijo<br />algo que dura<br />toda la vida
                </h2>
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white mb-4 sm:mb-6 md:mb-8 max-w-2xl leading-relaxed">
                  Más que un libro, "El poder de ser yo" es una invitación a criar desde el amor, la calma y la conexión emocional.
                </p>
                <p className="text-xl sm:text-2xl md:text-3xl text-white font-bold mb-6 sm:mb-8 md:mb-12">
                  ✨ Empieza hoy este viaje en familia por solo <span className="text-orange-400">$9 dólares</span> ✨
                </p>
              </div>
            </div>
          </div>

          {/* Bonuses */}
          <div className="max-w-4xl mx-auto mb-8 sm:mb-12 md:mb-16">
            <div className="bg-gradient-to-br from-orange-500 to-rose-500 rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-12 text-white">
              <div className="text-center mb-6 sm:mb-8">
                <div className="inline-block bg-white/20 backdrop-blur-sm px-4 sm:px-6 py-2 rounded-full mb-3 sm:mb-4">
                  <span className="text-white font-bold text-sm sm:text-base">🎁 BONOS INCLUIDOS</span>
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3">
                  Acceso completo + Bonos especiales
                </h3>
                <p className="text-base sm:text-lg text-white/90">
                  Todo lo que necesitas para empezar hoy mismo
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {[
                  { icon: 'ri-download-cloud-line', title: 'Descarga inmediata', desc: 'Acceso instantáneo al ebook completo en formato digital' },
                  { icon: 'ri-device-line', title: 'Multi-dispositivo', desc: 'Lee en tablet, móvil, computadora o imprime cuando quieras' },
                  { icon: 'ri-refresh-line', title: 'Actualizaciones gratis', desc: 'Recibe todas las actualizaciones futuras sin costo adicional' },
                  { icon: 'ri-heart-line', title: 'Soporte familiar', desc: 'Guía y recursos adicionales para toda la familia' }
                ].map((bonus, index) => (
                  <div
                    key={index}
                    className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-white/30 hover:bg-white/20 transition-all hover:scale-105"
                  >
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                      <i className={`${bonus.icon} text-2xl sm:text-3xl text-white`}></i>
                    </div>
                    <h4 className="font-bold text-lg sm:text-xl mb-2">{bonus.title}</h4>
                    <p className="text-sm sm:text-base text-white/90">{bonus.desc}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 sm:mt-8 text-center">
                <p className="text-base sm:text-lg font-bold mb-2">
                  Valor total: <span className="line-through text-white/70">$47</span>
                </p>
                <p className="text-2xl sm:text-3xl md:text-4xl font-bold">Hoy solo: $9 USD 🎉</p>
              </div>
            </div>
          </div>

          {/* Purchase Form */}
          {/* Removed purchase form section */}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <img
                  alt="Logo"
                  className="h-10 sm:h-12 w-auto"
                  src="https://public.readdy.ai/ai/img_res/8dee5cc1-3646-466d-be88-4d9c0487265e.png"
                />
                <h3 className="font-serif text-xl sm:text-2xl font-bold">
                  El poder de ser yo<span className="text-orange-400">.</span>
                </h3>
              </div>
              <h4 className="font-serif text-2xl sm:text-3xl md:text-4xl font-light leading-tight text-white mb-4 sm:mb-6">
                Crianza consciente<br />desde el corazón
              </h4>
              <p className="text-white/80 text-sm sm:text-base leading-relaxed max-w-md">
                Un ebook diseñado para fortalecer el amor propio, la calma y la conexión emocional en familia.
              </p>
            </div>

            <div>
              <h5 className="font-bold mb-3 sm:mb-4 text-base sm:text-lg text-white">Contenido</h5>
              <ul className="space-y-2">
                {[
                  { text: 'Cuentos', href: '#content' },
                  { text: 'Actividades', href: '#content' },
                  { text: 'Reflexiones', href: '#content' },
                  { text: 'Beneficios', href: '#benefits' }
                ].map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(link.href.substring(1));
                      }}
                      className="text-white/70 hover:text-white transition-colors cursor-pointer text-sm sm:text-base"
                    >
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h5 className="font-bold mb-3 sm:mb-4 text-base sm:text-lg text-white">Información</h5>
              <ul className="space-y-2">
                {[
                  { text: 'Preguntas frecuentes', href: '#faq' },
                  { text: 'Testimonios', href: '#testimonials' },
                  { text: 'Comprar', href: '#cta' },
                  { text: 'Contacto', href: 'mailto:contacto@ejemplo.com' }
                ].map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      onClick={(e) => {
                        if (link.href.startsWith('#')) {
                          e.preventDefault();
                          scrollToSection(link.href.substring(1));
                        }
                      }}
                      className="text-white/70 hover:text-white transition-colors cursor-pointer text-sm sm:text-base"
                    >
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="pt-6 sm:pt-8 border-t border-white/20">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
              <p className="text-white/70 text-xs sm:text-sm text-center md:text-left">
                © 2025 El poder de ser yo. Todos los derechos reservados.
              </p>
              <div className="flex gap-3 sm:gap-4">
                {['ri-instagram-line', 'ri-facebook-fill', 'ri-pinterest-fill', 'ri-youtube-fill'].map((icon, index) => (
                  <a
                    key={index}
                    href="#"
                    className="w-10 h-10 border-2 border-white/30 rounded-lg flex items-center justify-center hover:bg-gradient-to-r hover:from-orange-500 hover:to-rose-500 hover:border-transparent transition-all cursor-pointer hover:scale-110"
                  >
                    <i className={`${icon} text-lg`}></i>
                  </a>
                ))}
              </div>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/70 text-xs sm:text-sm hover:text-white transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
